// <editorial-chapter> — parallax + mask reveal + image carousel for the About page chapter sections.
// Uses plain HTMLElement (no refs / shadow DOM) — keep this lean.

class EditorialChapter extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {HTMLElement | null} */
  #imageEl = null;
  #ticking = false;
  #reducedMotion = false;

  // Carousel state
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {NodeListOf<HTMLElement> | null} */
  #slides = null;
  /** @type {NodeListOf<HTMLElement> | null} */
  #dots = null;
  #currentSlide = 0;
  #touchStartX = 0;
  #touchDeltaX = 0;
  #isDragging = false;

  connectedCallback() {
    this.#imageEl = this.querySelector('[data-ec-image]');
    this.#reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.#observeReveal();

    if (!this.#reducedMotion && this.#imageEl) {
      const { signal } = this.#controller;
      window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
      window.addEventListener('resize', this.#onScroll, { passive: true, signal });
      this.#updateParallax();
    }

    // Initialize carousel if present
    this.#initCarousel();
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#controller.abort();
  }

  #observeReveal() {
    if (!('IntersectionObserver' in window)) {
      this.classList.add('is-revealed');
      return;
    }

    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
            this.classList.add('is-revealed');
            this.#io?.disconnect();
            this.#io = null;
            return;
          }
        }
      },
      { threshold: [0, 0.15, 0.3] }
    );

    this.#io.observe(this);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#updateParallax();
      this.#ticking = false;
    });
  };

  #updateParallax() {
    if (!this.#imageEl) return;
    const rect = this.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;

    if (rect.bottom < -200 || rect.top > vh + 200) return;

    const total = vh + rect.height;
    const passed = vh - rect.top;
    const progress = Math.max(0, Math.min(1, passed / total));
    const offset = (progress - 0.5) * 40;

    this.#imageEl.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
  }

  // ===== Carousel =====

  #initCarousel() {
    const carousel = this.querySelector('[data-ec-carousel]');
    if (!carousel) return;

    this.#track = this.querySelector('[data-ec-track]');
    this.#slides = this.querySelectorAll('[data-ec-slide]');
    this.#dots = this.querySelectorAll('[data-ec-dot]');

    if (!this.#track || !this.#slides || this.#slides.length < 2) return;

    const { signal } = this.#controller;

    // Arrow buttons
    const prevBtn = this.querySelector('[data-ec-prev]');
    const nextBtn = this.querySelector('[data-ec-next]');

    prevBtn?.addEventListener('click', () => this.#goTo(this.#currentSlide - 1), { signal });
    nextBtn?.addEventListener('click', () => this.#goTo(this.#currentSlide + 1), { signal });

    // Dot buttons
    this.#dots?.forEach((dot) => {
      dot.addEventListener('click', () => {
        const idx = parseInt(dot.getAttribute('data-ec-dot'), 10);
        if (!isNaN(idx)) this.#goTo(idx);
      }, { signal });
    });

    // Touch / pointer support for swipe
    carousel.addEventListener('pointerdown', this.#onPointerDown, { signal });
    carousel.addEventListener('pointermove', this.#onPointerMove, { signal });
    carousel.addEventListener('pointerup', this.#onPointerUp, { signal });
    carousel.addEventListener('pointercancel', this.#onPointerUp, { signal });

    // Keyboard support
    carousel.setAttribute('tabindex', '0');
    carousel.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.#goTo(this.#currentSlide - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.#goTo(this.#currentSlide + 1); }
    }, { signal });
  }

  #goTo(index) {
    if (!this.#slides || !this.#track) return;
    const total = this.#slides.length;

    // Clamp to valid range
    if (index < 0) index = total - 1;
    if (index >= total) index = 0;

    this.#currentSlide = index;
    this.#track.style.transform = `translateX(-${index * 100}%)`;

    // Update dots
    this.#dots?.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });
  }

  #onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.#isDragging = true;
    this.#touchStartX = e.clientX;
    this.#touchDeltaX = 0;
    if (this.#track) {
      this.#track.style.transition = 'none';
    }
  };

  #onPointerMove = (e) => {
    if (!this.#isDragging) return;
    this.#touchDeltaX = e.clientX - this.#touchStartX;

    if (this.#track && this.#slides) {
      const baseOffset = -this.#currentSlide * 100;
      const carousel = this.querySelector('[data-ec-carousel]');
      const width = carousel?.offsetWidth || 1;
      const dragPercent = (this.#touchDeltaX / width) * 100;
      this.#track.style.transform = `translateX(${baseOffset + dragPercent}%)`;
    }
  };

  #onPointerUp = () => {
    if (!this.#isDragging) return;
    this.#isDragging = false;

    if (this.#track) {
      this.#track.style.transition = '';
    }

    const threshold = 50; // px
    if (this.#touchDeltaX < -threshold) {
      this.#goTo(this.#currentSlide + 1);
    } else if (this.#touchDeltaX > threshold) {
      this.#goTo(this.#currentSlide - 1);
    } else {
      this.#goTo(this.#currentSlide); // snap back
    }
  };
}

if (!customElements.get('editorial-chapter')) {
  customElements.define('editorial-chapter', EditorialChapter);
}
